import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { axiosPrivate } from "../../api/axios";
import { 
  Box, 
  Button, 
  Card, 
  CardHeader, 
  CardContent, 
  useTheme, 
  Tabs, 
  Tab,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MasterAgGrid from '../common/DataGrid/MasterAgGrid';
import MasterDataDialog from './MasterDataDialog';
import { useTranslation } from 'react-i18next';

const WorkerMasterPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const theme = useTheme();
  const { group_code, project_code } = useParams();
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // 탭 관련 상태
  const tabs = useMemo(() => ([
    { id: 'workers', name: t('pages.master.worker.tabs.workerManagement') },
    { id: 'worker-teams', name: t('pages.master.worker.tabs.teamManagement', '부서 관리') },
    { id: 'worker-positions', name: t('pages.master.worker.tabs.positionManagement', '직급 관리') },
  ]), [t]);
  const [selectedTab, setSelectedTab] = useState('workers');

  const [projectName, setProjectName] = useState('');

  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [dataDialogMode, setDataDialogMode] = useState('add'); // 'add' 또는 'edit'
  const [currentData, setCurrentData] = useState(null);
  const [workerTeams, setWorkerTeams] = useState([]);
  const [workerPositions, setWorkerPositions] = useState([]);

  // 작업자 부서/직급 목록 재조회 (다이얼로그 옵션 최신화용)
  const refreshMasterLists = useCallback(async () => {
    try {
      const [teamsResponse, positionsResponse] = await Promise.all([
        axiosPrivate.post('/master/worker-teams', { group_code, project_code }),
        axiosPrivate.post('/master/worker-positions', { group_code, project_code })
      ]);
      setWorkerTeams(teamsResponse.data);
      setWorkerPositions(positionsResponse.data);
    } catch (err) {
      console.error("작업자 부서/직급 목록 로드 실패:", err);
    }
  }, [group_code, project_code]);

  useEffect(() => {
    document.title = t('pages.master.worker.title');
  }, [t]);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let endpoint = '';
      if (selectedTab === 'workers') {
        endpoint = '/master/workers';
      } else if (selectedTab === 'worker-teams') {
        endpoint = '/master/worker-teams';
      } else if (selectedTab === 'worker-positions') {
        endpoint = '/master/worker-positions';
      }
      // 다른 탭 추가 시 else if 로 분기
      
      if (!endpoint) return;

      const response = await axiosPrivate.post(endpoint, {
        group_code,
        project_code
      });

      if (selectedTab === 'workers') {
        setColumnDefs([
          { field: 'worker_id', headerName: t('pages.master.worker.columns.workerId'), editable: false, hide: true },
          { field: 'worker_name', headerName: t('pages.master.worker.columns.name'), editable: false },
          { field: 'team_name', headerName: t('pages.master.worker.columns.team', '부서'), editable: false, valueFormatter: p => p.value || '-' },
          { field: 'position_name', headerName: t('pages.master.worker.columns.position', '직급'), editable: false, valueFormatter: p => p.value || '-' },
          { field: 'updateDate', headerName: t('pages.master.worker.columns.updateDate'), editable: false }
        ]);
      } else if (selectedTab === 'worker-teams') {
        setColumnDefs([
          { field: 'team_id', headerName: t('pages.master.worker.columns.teamId', '부서ID'), editable: false, hide: true },
          { field: 'team_name', headerName: t('pages.master.worker.columns.teamName', '부서명'), editable: false },
        ]);
      } else if (selectedTab === 'worker-positions') {
        setColumnDefs([
          { field: 'position_id', headerName: t('pages.master.worker.columns.positionId', '직급ID'), editable: false, hide: true },
          { field: 'position_name', headerName: t('pages.master.worker.columns.positionName', '직급명'), editable: false },
        ]);
      }
      
      const dataWithId = response.data.map((row, index) => {
        if (selectedTab === 'workers') {
          return {
            ...row,
            id: row.worker_id || index + 1,
            updateDate: row.updateDate || new Date().toISOString()
          };
        }
        if (selectedTab === 'worker-teams') {
          return { ...row, id: row.team_id || index + 1 };
        }
        if (selectedTab === 'worker-positions') {
          return { ...row, id: row.position_id || index + 1 };
        }
        return { ...row, id: index + 1 };
      });
  
      setRowData(dataWithId);
    } catch (err) {
      console.error("데이터 조회 실패:", err);
      setError(t('pages.master.worker.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, selectedTab, t]);

  useEffect(() => {
      fetchData();
  }, [fetchData, selectedTab]);

  // 부서/직급 목록 로드 (작업자 다이얼로그용)
  useEffect(() => {
    refreshMasterLists();
  }, [refreshMasterLists, group_code, project_code]);

  const handleAddRow = () => {
    refreshMasterLists();
    setDataDialogMode('add');
    setCurrentData(null);
    setDataDialogOpen(true);
  };

  const handleDeleteRow = async () => {
    if (selectedRows.length === 0) {
      return;
    }
    
    if (window.confirm(t('pages.master.worker.messages.deleteConfirm', { count: selectedRows.length }))) {
      setLoading(true);
      
      try {
        let endpoint = '';
        let idField = '';

        if (selectedTab === 'workers') {
          endpoint = '/master/workers/delete';
          idField = 'worker_id';
        } else if (selectedTab === 'worker-teams') {
          endpoint = '/master/worker-teams/delete';
          idField = 'team_id';
        } else if (selectedTab === 'worker-positions') {
          endpoint = '/master/worker-positions/delete';
          idField = 'position_id';
        }
        
        const ids = selectedRows.map(row => row[idField]);
        
        await axiosPrivate.post(endpoint, {
          group_code,
          project_code,
          ids
        });
        
        await fetchData();
        setSelectedRows([]);
        alert(t('pages.master.worker.messages.deleteSuccess', { count: ids.length }));
      } catch (err) {
        console.error("데이터 삭제 실패:", err);
        setError(t('pages.master.worker.messages.deleteError'));
      } finally {
        setLoading(false);
      }
    }
  };

  const onSelectionChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
    }
  };

  const handleBackToProjects = () => {
    navigate('/projects');
  };

  // 탭 변경 핸들러
  const handleTabChange = async (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 'workers') {
      await refreshMasterLists();
    }
    setTimeout(() => {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.sizeColumnsToFit();
        gridRef.current.api.autoSizeAllColumns();
      }
    }, 200);
  };

  const getPageTitle = () => {
    const tab = tabs.find(t => t.id === selectedTab);
    const tabName = tab ? tab.name : t('pages.master.worker.title');
    
    if (projectName) {
      return `${tabName} (${projectName})`;
    }
    return tabName;
  };

  const handleRowDoubleClicked = (params) => {
    setDataDialogMode('edit');
    setCurrentData(params.data);
    setDataDialogOpen(true);
  };

  const handleSaveData = async (data, mode) => {
    setLoading(true);
    
    try {
      let endpoint = '';
      
      if (mode === 'add') {
        if (selectedTab === 'workers') {
          endpoint = '/master/workers/add';
        } else if (selectedTab === 'worker-teams') {
          endpoint = '/master/worker-teams/add';
        } else if (selectedTab === 'worker-positions') {
          endpoint = '/master/worker-positions/add';
        }
      } else if (mode === 'edit') {
        if (selectedTab === 'workers') {
          endpoint = '/master/workers/update';
        } else if (selectedTab === 'worker-teams') {
          endpoint = '/master/worker-teams/update';
        } else if (selectedTab === 'worker-positions') {
          endpoint = '/master/worker-positions/update';
        }
      }
      
      await axiosPrivate.post(endpoint, {
        group_code,
        project_code,
        data
      });
      
      await fetchData();
      await refreshMasterLists();
      setDataDialogOpen(false);
      alert(mode === 'add' ? t('pages.master.worker.messages.addSuccess') : t('pages.master.worker.messages.editSuccess'));
      
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.sizeColumnsToFit();
          gridRef.current.api.autoSizeAllColumns();
        }
      }, 100);
    } catch (err) {
      console.error("데이터 저장 실패:", err);
      alert(t('pages.master.worker.messages.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDataDialogOpen(false);
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title={getPageTitle()}
            sx={{
              '& .MuiCardHeader-title': {
                fontSize: '22px',
                fontWeight: 600
              },
              paddingBlock: '16px'
            }}
            action={
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToProjects}
              >
                {t('pages.master.worker.buttons.projectList')}
              </Button>
            }
          />
          
          {/* 탭 네비게이션 */}
          <Box sx={{ 
            px: 6
          }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: '12px 16px',
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  },
                }
              }}
            >
              {tabs.map((tab) => (
                <Tab 
                  key={tab.id} 
                  value={tab.id} 
                  label={tab.name}
                  sx={{ 
                    minHeight: '48px',
                    '&.MuiButtonBase-root:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>
          
          {error && (
            <Box sx={{ px: 6, pt: 2 }}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Box>
          )}
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 2,
            px: 6,
            pt: 2
          }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              disabled={loading}
            >
              {t('pages.master.worker.buttons.add')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteRow}
              disabled={selectedRows.length === 0 || loading}
            >
              {t('pages.master.worker.buttons.delete')}
            </Button>
          </Box>

          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            paddingInline: 6,
            paddingBlock: 2,
            height: 0
          }}>
            <MasterAgGrid
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              pageSize={pageSize}
              loading={loading}
              onRowDoubleClicked={handleRowDoubleClicked}
            />
          </CardContent>
        </Card>
      </Grid>
      
      <MasterDataDialog
        open={dataDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveData}
        data={currentData}
        mode={dataDialogMode}
        type={selectedTab}
        workerTeams={workerTeams}
        workerPositions={workerPositions}
        group_code={group_code}
        project_code={project_code}
      />
    </Grid>
  );
};

export default WorkerMasterPage;